/**
 * Planilhas .xlsx sem biblioteca externa e sem rede (portado da DEMO-03,
 * frontend/prototipo/src/planilha.ts), usado no navegador e no servidor:
 *  - gerarXlsx: pacote ZIP (sem compressão) com o mínimo do formato OOXML;
 *  - lerXlsx: lê ZIP com ou sem compressão (deflate via DecompressionStream).
 * No servidor o arquivo vem de fora: a leitura tem LIMITES (entradas, tamanho
 * descompactado por entrada e total) contra arquivos malformados ou "bombas"
 * de descompactação.
 */
export const LIMITES_ZIP = { entradas: 200, porEntrada: 20 * 1024 * 1024, total: 40 * 1024 * 1024 };

/* ---------------- ZIP mínimo ---------------- */
const TABELA_CRC = (() => {
  const t = new Uint32Array(256);
  for (let n = 0; n < 256; n++) {
    let c = n;
    for (let k = 0; k < 8; k++) c = c & 1 ? 0xedb88320 ^ (c >>> 1) : c >>> 1;
    t[n] = c >>> 0;
  }
  return t;
})();
export function crc32(b: Uint8Array) {
  let c = 0xffffffff;
  for (let i = 0; i < b.length; i++) c = TABELA_CRC[(c ^ b[i]) & 0xff] ^ (c >>> 8);
  return (c ^ 0xffffffff) >>> 0;
}
const u16 = (v: number) => [v & 0xff, (v >>> 8) & 0xff];
const u32 = (v: number) => [v & 0xff, (v >>> 8) & 0xff, (v >>> 16) & 0xff, (v >>> 24) & 0xff];
export function zip(arquivos: { nome: string; dados: Uint8Array }[]): Uint8Array {
  const enc = new TextEncoder();
  const partes: number[] = [];
  const central: number[] = [];
  for (const a of arquivos) {
    const nome = enc.encode(a.nome);
    const crc = crc32(a.dados);
    const deslocamento = partes.length;
    partes.push(...u32(0x04034b50), ...u16(20), ...u16(0x0800), ...u16(0), ...u16(0), ...u16(0x21), ...u32(crc), ...u32(a.dados.length), ...u32(a.dados.length), ...u16(nome.length), ...u16(0), ...nome, ...a.dados);
    central.push(...u32(0x02014b50), ...u16(20), ...u16(20), ...u16(0x0800), ...u16(0), ...u16(0), ...u16(0x21), ...u32(crc), ...u32(a.dados.length), ...u32(a.dados.length), ...u16(nome.length), ...u16(0), ...u16(0), ...u16(0), ...u16(0), ...u32(0), ...u32(deslocamento), ...nome);
  }
  const fim = [...u32(0x06054b50), ...u16(0), ...u16(0), ...u16(arquivos.length), ...u16(arquivos.length), ...u32(central.length), ...u32(partes.length), ...u16(0)];
  return new Uint8Array([...partes, ...central, ...fim]);
}
/** Descompacta em fluxo e INTERROMPE assim que passar do limite (não aloca a "bomba" inteira). */
async function descompactar(dados: Uint8Array, limite = LIMITES_ZIP.porEntrada): Promise<Uint8Array> {
  const Ds = (globalThis as { DecompressionStream?: new (f: string) => TransformStream<Uint8Array, Uint8Array> }).DecompressionStream;
  if (!Ds) throw new Error('Este ambiente não consegue ler planilhas compactadas.');
  const leitor = new Response(dados as unknown as ArrayBuffer).body!.pipeThrough(new Ds('deflate-raw')).getReader();
  const partes: Uint8Array[] = [];
  let total = 0;
  for (;;) {
    const { done, value } = await leitor.read();
    if (done) break;
    total += value.length;
    if (total > limite) {
      await leitor.cancel();
      throw new Error('A planilha descompactada é grande demais.');
    }
    partes.push(value);
  }
  const saida = new Uint8Array(total);
  let pos = 0;
  for (const p of partes) (saida.set(p, pos), (pos += p.length));
  return saida;
}
/** Lê as entradas de um ZIP (métodos 0 e 8) pelo diretório central. */
export async function unzip(b: Uint8Array): Promise<Map<string, Uint8Array>> {
  if (b.length < 22) throw new Error('O arquivo não é uma planilha .xlsx válida.');
  const dv = new DataView(b.buffer, b.byteOffset, b.byteLength);
  let fim = -1;
  for (let i = b.length - 22; i >= Math.max(0, b.length - 65_557); i--) if (dv.getUint32(i, true) === 0x06054b50) (fim = i), (i = -1);
  if (fim < 0) throw new Error('O arquivo não é uma planilha .xlsx válida.');
  const total = dv.getUint16(fim + 10, true);
  if (total > LIMITES_ZIP.entradas) throw new Error('A planilha tem partes demais.');
  let acumulado = 0;
  let p = dv.getUint32(fim + 16, true);
  const dec = new TextDecoder();
  const saida = new Map<string, Uint8Array>();
  for (let k = 0; k < total; k++) {
    const metodo = dv.getUint16(p + 10, true);
    const tamC = dv.getUint32(p + 20, true);
    const tamD = dv.getUint32(p + 24, true);
    if (tamD > LIMITES_ZIP.porEntrada || (acumulado += tamD) > LIMITES_ZIP.total) throw new Error('A planilha descompactada é grande demais.');
    const nLen = dv.getUint16(p + 28, true);
    const eLen = dv.getUint16(p + 30, true);
    const cLen = dv.getUint16(p + 32, true);
    const local = dv.getUint32(p + 42, true);
    const nome = dec.decode(b.subarray(p + 46, p + 46 + nLen));
    const inicio = local + 30 + dv.getUint16(local + 26, true) + dv.getUint16(local + 28, true);
    const bruto = b.subarray(inicio, inicio + tamC);
    const dados = metodo === 0 ? bruto : metodo === 8 ? await descompactar(bruto) : new Uint8Array();
    if (dados.length > LIMITES_ZIP.porEntrada) throw new Error('A planilha descompactada é grande demais.');
    saida.set(nome, dados);
    p += 46 + nLen + eLen + cLen;
  }
  return saida;
}

/* ---------------- XLSX ---------------- */
const esc = (s: string) => s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
const letra = (i: number) => (i < 26 ? String.fromCharCode(65 + i) : String.fromCharCode(64 + Math.floor(i / 26)) + String.fromCharCode(65 + (i % 26)));
/** Gera um .xlsx de uma aba com as linhas informadas (texto; números inteiros como número). */
export function gerarXlsx(linhas: (string | number)[][], aba = 'Planilha'): Uint8Array {
  const enc = new TextEncoder();
  const celula = (v: string | number, r: number, c: number) => (typeof v === 'number' ? `<c r="${letra(c)}${r}"><v>${v}</v></c>` : `<c r="${letra(c)}${r}" t="inlineStr"><is><t xml:space="preserve">${esc(v)}</t></is></c>`);
  const sheet = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?><worksheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main"><sheetData>${linhas.map((l, i) => `<row r="${i + 1}">${l.map((v, c) => celula(v, i + 1, c)).join('')}</row>`).join('')}</sheetData></worksheet>`;
  const arquivos: [string, string][] = [
    ['[Content_Types].xml', '<?xml version="1.0" encoding="UTF-8" standalone="yes"?><Types xmlns="http://schemas.openxmlformats.org/package/2006/content-types"><Default Extension="rels" ContentType="application/vnd.openxmlformats-package.relationships+xml"/><Default Extension="xml" ContentType="application/xml"/><Override PartName="/xl/workbook.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet.main+xml"/><Override PartName="/xl/worksheets/sheet1.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.worksheet+xml"/><Override PartName="/xl/styles.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.styles+xml"/></Types>'],
    ['_rels/.rels', '<?xml version="1.0" encoding="UTF-8" standalone="yes"?><Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships"><Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/officeDocument" Target="xl/workbook.xml"/></Relationships>'],
    ['xl/workbook.xml', `<?xml version="1.0" encoding="UTF-8" standalone="yes"?><workbook xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main" xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships"><sheets><sheet name="${esc(aba)}" sheetId="1" r:id="rId1"/></sheets></workbook>`],
    ['xl/_rels/workbook.xml.rels', '<?xml version="1.0" encoding="UTF-8" standalone="yes"?><Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships"><Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/worksheet" Target="worksheets/sheet1.xml"/><Relationship Id="rId2" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/styles" Target="styles.xml"/></Relationships>'],
    ['xl/styles.xml', '<?xml version="1.0" encoding="UTF-8" standalone="yes"?><styleSheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main"><fonts count="1"><font><sz val="11"/><name val="Arial"/></font></fonts><fills count="1"><fill><patternFill patternType="none"/></fill></fills><borders count="1"><border/></borders><cellStyleXfs count="1"><xf/></cellStyleXfs><cellXfs count="1"><xf/></cellXfs></styleSheet>'],
    ['xl/worksheets/sheet1.xml', sheet],
  ];
  return zip(arquivos.map(([nome, xml]) => ({ nome, dados: enc.encode(xml) })));
}
const desfazer = (s: string) => s.replace(/&lt;/g, '<').replace(/&gt;/g, '>').replace(/&quot;/g, '"').replace(/&apos;/g, "'").replace(/&amp;/g, '&');
/** Lê a primeira aba de um .xlsx e devolve as linhas (texto), respeitando a posição das colunas. */
export async function lerXlsx(b: Uint8Array): Promise<string[][]> {
  const z = await unzip(b);
  const dec = new TextDecoder();
  const txt = (n: string) => (z.has(n) ? dec.decode(z.get(n)) : '');
  const compartilhadas = [...txt('xl/sharedStrings.xml').matchAll(/<si>([\s\S]*?)<\/si>/g)].map((m) => desfazer([...m[1].matchAll(/<t[^>]*>([\s\S]*?)<\/t>/g)].map((x) => x[1]).join('')));
  const nomeAba = [...z.keys()].filter((k) => /^xl\/worksheets\/sheet\d+\.xml$/.test(k)).sort()[0];
  if (!nomeAba) throw new Error('A planilha não tem abas legíveis.');
  const linhas: string[][] = [];
  for (const r of txt(nomeAba).matchAll(/<row[^>]*>([\s\S]*?)<\/row>/g)) {
    const linha: string[] = [];
    for (const c of r[1].matchAll(/<c([^>]*?)(?:\/>|>([\s\S]*?)<\/c>)/g)) {
      const attrs = c[1];
      const ref = attrs.match(/r="([A-Z]+)\d+"/)?.[1] ?? letra(linha.length);
      const col = [...ref].reduce((s, ch) => s * 26 + ch.charCodeAt(0) - 64, 0) - 1;
      const tipo = attrs.match(/t="(\w+)"/)?.[1];
      const corpo = c[2] ?? '';
      const v = corpo.match(/<v>([\s\S]*?)<\/v>/)?.[1];
      const valor = tipo === 's' ? (compartilhadas[Number(v)] ?? '') : tipo === 'inlineStr' ? desfazer([...corpo.matchAll(/<t[^>]*>([\s\S]*?)<\/t>/g)].map((x) => x[1]).join('')) : desfazer(v ?? '');
      linha[col] = valor;
    }
    linhas.push(Array.from({ length: linha.length }, (_, i) => linha[i] ?? ''));
  }
  return linhas;
}

